import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { FocusTrapFactory } from '@angular/cdk/a11y';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { KeyboardKeys } from '../../helpers/KeyboardKeys.enum';

@Component({
  selector: 'phx-sheet',
  templateUrl: './phx-sheet.component.html',
  styleUrls: ['./phx-sheet.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'phx-sheet',
  },
})
export class PhxSheetComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() phxExpanded: boolean = false;
  @Input() phxPeekHeight: number = 92;
  @Input() phxTopOffset: number = 80;
  @Input() phxBackdrop: boolean = true;
  @Input() phxAutoClose: boolean = true;

  @Output() phxExpandedChange = new EventEmitter<boolean>();
  @Output() phxBackdropClicked = new EventEmitter<void>();
  /** Emitted when a drag would collapse expanded → peek while `phxAutoClose` is false; parent may confirm then set `phxExpanded` to false. */
  @Output() phxDismissAttempt = new EventEmitter<void>();

  @ViewChild('sheetTemplate') private readonly _sheetTemplate: TemplateRef<HTMLElement>;

  private readonly _destroy$ = new Subject<void>();

  private _overlayRef: OverlayRef | null = null;
  private _portal: TemplatePortal | null = null;
  private _focusTrap: ReturnType<FocusTrapFactory['create']> | null = null;
  private _isViewInitialized = false;

  /** Pointer drag state */
  _dragHeight: number | null = null;
  private _heightAtDragStart = 0;
  private _pointerStartY = 0;
  private _pointerStartX = 0;
  private _isDragging = false;
  private _bodyScrollTopAtStart = 0;
  private _dragOrigin: 'handle' | 'body' = 'handle';
  private _lastPointerY = 0;
  private _lastPointerTime = 0;
  private _lastTouchY = 0;
  private _bodyEl: HTMLElement | null = null;
  /** Unlisten functions from `Renderer2.listen`; cleared in teardown / `ngOnDestroy`. */
  private _resizeUnlisten: (() => void) | null = null;
  private _panelPointerUnlistens: (() => void)[] = [];
  private _bodyTouchUnlistens: (() => void)[] = [];
  /** Set in `_startDrag` when `setPointerCapture` succeeds; cleared in `_onPointerEnd`. */
  private _capturedPointerId: number | null = null;
  /** Sheet panel root; set in `_createOverlay` after portal attach. */
  private _panelEl: HTMLElement | null = null;

  constructor(
    private readonly _cdr: ChangeDetectorRef,
    private readonly _focusTrapFactory: FocusTrapFactory,
    private readonly _overlay: Overlay,
    private readonly _renderer: Renderer2,
    private readonly _viewContainer: ViewContainerRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!this._isViewInitialized) return;

    const { phxExpanded } = changes;

    if (phxExpanded) {
      this._applyExpandedState(false);
    }
  }

  ngAfterViewInit(): void {
    this._isViewInitialized = true;
    this._createOverlay();
    this._setupResizeListener();
    this._applyExpandedState(false);
    this._cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this._releasePointerCaptureIfAny();
    this._removePointerListeners();
    this._panelEl = null;
    this._removeResizeListener();
    this._teardownBodyTouchListeners();
    this._destroyFocusTrap();
    this._setPageScrollLocked(false);
    this._overlayRef?.dispose();
    this._destroy$.next();
    this._destroy$.complete();
  }

  get panelHeight(): number {
    if (this._dragHeight !== null) return this._dragHeight;
    return this.phxExpanded ? this._maxHeight : this.phxPeekHeight;
  }

  private get _maxHeight(): number {
    if (typeof window === 'undefined') return this.phxPeekHeight;
    return Math.max(this.phxPeekHeight, window.innerHeight - this.phxTopOffset);
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key !== KeyboardKeys.ESC || !this.phxExpanded) return;
    if (!this.phxAutoClose) return;
    event.preventDefault();
    this.setExpanded(false);
  }

  onPanelPointerDown(event: PointerEvent): void {
    if (event.button !== 0) return;
    const target = event.target as HTMLElement | null;
    if (!target) return;

    if (target.closest('[phx-sheet-drag-handle]')) {
      this._startDrag(event, 'handle');
      return;
    }

    if (target.closest('[phx-sheet-drag-area]')) {
      this._onBodyPointerDown(event);
    }
  }

  setExpanded(expanded: boolean): void {
    if (this.phxExpanded === expanded) return;
    this.phxExpanded = expanded;
    this._applyExpandedState(true);
  }

  private _applyExpandedState(emit: boolean): void {
    this._syncBackdropVisibility();
    this._setPageScrollLocked(this.phxExpanded);

    if (this.phxExpanded) {
      this._createFocusTrap();
      this._panelEl?.focus();
    } else {
      this._destroyFocusTrap();
    }

      if (!this._bodyEl) {
        this._bodyEl = this._panelEl?.querySelector<HTMLElement>('[phx-sheet-drag-area]') ?? null;
      }
      this._setupBodyTouchListeners();

    if (emit) this.phxExpandedChange.emit(this.phxExpanded);

    this._cdr.markForCheck();
  }

  private _createOverlay(): void {
    if (this._overlayRef || !this._sheetTemplate) return;

    const overlayConfig = new OverlayConfig({
      hasBackdrop: this.phxBackdrop,
      backdropClass: ['phx-sheet-backdrop', 'phx-sheet-backdrop-hidden'],
      positionStrategy: this._overlay.position().global().left('0').right('0').bottom('0'),
      scrollStrategy: this._overlay.scrollStrategies.noop(),
      panelClass: 'phx-sheet-overlay-pane',
      width: '100%',
    });

    this._overlayRef = this._overlay.create(overlayConfig);
    this._portal = new TemplatePortal(this._sheetTemplate, this._viewContainer);
    this._overlayRef.attach(this._portal);

    const overlayPane = this._overlayRef.overlayElement;
    this._panelEl = overlayPane.querySelector<HTMLElement>('.phx-sheet-panel');
    if (this._panelEl) {
      this._setupPointerListeners();
    }

    this._overlayRef
      .backdropClick()
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.phxBackdropClicked.emit();
        if (this.phxAutoClose) {
          this.setExpanded(false);
        }
      });
  }

  private _syncBackdropVisibility(): void {
    const backdropElement = this._overlayRef?.backdropElement;
    if (!backdropElement) return;
    const hiddenClass = 'phx-sheet-backdrop-hidden';
    if (!this.phxExpanded) {
      this._renderer.addClass(backdropElement, hiddenClass);
    } else {
      this._renderer.removeClass(backdropElement, hiddenClass);
    }
  }

  private _createFocusTrap(): void {
    const panel = this._panelEl;
    if (!panel || this._focusTrap) return;
    this._focusTrap = this._focusTrapFactory.create(panel);
  }

  private _destroyFocusTrap(): void {
    if (!this._focusTrap) return;
    this._focusTrap.destroy();
    this._focusTrap = null;
  }

  private _setPageScrollLocked(locked: boolean): void {
    if (typeof document === 'undefined') return;
    const body = document.body;
    const lockClass = 'phx-sheet-scroll-lock';
    if (locked) {
      this._renderer.addClass(body, lockClass);
    } else {
      this._renderer.removeClass(body, lockClass);
    }
  }

  private _setupBodyTouchListeners(): void {
    const body = this._bodyEl;
    if (!body) return;

    this._teardownBodyTouchListeners();

    if (this.phxExpanded) {
      this._bodyTouchUnlistens = [
        this._renderer.listen(body, 'touchstart', (event: Event) => {
          const e = event as TouchEvent;
          this._lastTouchY = e.touches[0].clientY;
        }),
        this._renderer.listen(body, 'touchmove', (event: Event) => {
          const e = event as TouchEvent;
          const currentY = e.touches[0].clientY;
          const isDraggingDown = currentY > this._lastTouchY;
          const isAtTop = body.scrollTop <= 1;
          
          if (isAtTop && isDraggingDown) {
            e.preventDefault();
          }

          this._lastTouchY = currentY;
        }),
      ];
    }
  }

  private _teardownBodyTouchListeners(): void {
    for (const unlisten of this._bodyTouchUnlistens) {
      unlisten();
    }
    this._bodyTouchUnlistens = [];
  }

  private _setupPointerListeners(): void {
    const panel = this._panelEl;
    if (!panel) return;

    this._removePointerListeners();
    this._panelPointerUnlistens = [
      this._renderer.listen(panel, 'pointermove', (event: Event) => this._onPointerMove(event as PointerEvent)),
      this._renderer.listen(panel, 'pointerup', (event: Event) => this._onPointerEnd(event as PointerEvent)),
      this._renderer.listen(panel, 'pointercancel', (event: Event) => this._onPointerEnd(event as PointerEvent)),
    ];
  }

  private _removePointerListeners(): void {
    for (const unlisten of this._panelPointerUnlistens) {
      unlisten();
    }
    this._panelPointerUnlistens = [];
  }

  private _setupResizeListener(): void {
    this._removeResizeListener();
    if (typeof window === 'undefined') return;
    this._resizeUnlisten = this._renderer.listen('window', 'resize', () => this._onViewportResize());
  }

  private _removeResizeListener(): void {
    this._resizeUnlisten?.();
    this._resizeUnlisten = null;
  }

  private _onViewportResize(): void {
    if (this._dragHeight !== null) {
      this._dragHeight = Math.max(this.phxPeekHeight, Math.min(this._dragHeight, this._maxHeight));
    }
    this._overlayRef?.updatePosition();
    this._cdr.markForCheck();
  }

  private _startDrag(event: PointerEvent, origin: 'handle' | 'body'): void {
    event.preventDefault();
    const panel = this._panelEl;
    if (panel) {
      try {
        panel.setPointerCapture(event.pointerId);
        this._capturedPointerId = event.pointerId;
      } catch {
        this._capturedPointerId = null;
      }
    } else {
      this._capturedPointerId = null;
    }

    this._isDragging = true;
    this._dragOrigin = origin;
    this._pointerStartY = event.clientY;
    this._pointerStartX = event.clientX;
    this._heightAtDragStart = this.panelHeight;
    this._lastPointerY = event.clientY;
    this._lastPointerTime = event.timeStamp;
  }

  private _onBodyPointerDown(event: PointerEvent): void {
    const body = this._bodyEl;
    if (!body) return;

    this._bodyScrollTopAtStart = body.scrollTop;
    if (body.scrollTop > 1) return;

    this._startDrag(event, 'body');
  }

  private _onPointerMove(event: PointerEvent): void {
    if (!this._isDragging) return;

    const body = this._bodyEl;
    if (body && this._bodyScrollTopAtStart > 1) {
      const bodyRect = body.getBoundingClientRect();
      const isInBody =
        event.clientY >= bodyRect.top &&
        event.clientY <= bodyRect.bottom &&
        event.clientX >= bodyRect.left &&
        event.clientX <= bodyRect.right;

      if (isInBody && body.scrollTop > 1) {
        return;
      }
    }

    this._lastPointerY = event.clientY;
    this._lastPointerTime = event.timeStamp;

    const deltaY = event.clientY - this._pointerStartY;
    const newHeight = this._heightAtDragStart - deltaY;
    this._dragHeight = Math.max(this.phxPeekHeight, Math.min(newHeight, this._maxHeight));
    this._cdr.detectChanges();
  }

  private _onPointerEnd(event: PointerEvent): void {
    if (!this._isDragging) return;

    if (this._dragHeight !== null) {
      const dt = event.timeStamp - this._lastPointerTime;
      const dy = this._lastPointerY - event.clientY;
      const velocity = dt > 0 ? dy / dt : 0;
      const VELOCITY_THRESHOLD = 0.1; // pixels per millisecond

      // Net displacement: positive = swiped up, negative = swiped down
      const displacement = this._pointerStartY - event.clientY;
      const DISPLACEMENT_THRESHOLD = 30;

      let shouldExpand: boolean;
      if (Math.abs(velocity) > VELOCITY_THRESHOLD) {
        shouldExpand = velocity > 0;
      } else if (Math.abs(displacement) > DISPLACEMENT_THRESHOLD) {
        shouldExpand = displacement > 0;
      } else {
        // Tiny movement — keep current state
        shouldExpand = this.phxExpanded;
      }

      this._dragHeight = null;
      if (this.phxExpanded === shouldExpand) {
        this._cdr.detectChanges();
      } else if (!shouldExpand && this.phxExpanded && !this.phxAutoClose) {
        this.phxDismissAttempt.emit();
        this._cdr.detectChanges();
      } else {
        this.setExpanded(shouldExpand);
      }
    } else if (this._dragOrigin === 'handle') {
      const deltaX = Math.abs(event.clientX - this._pointerStartX);
      const deltaY = Math.abs(event.clientY - this._pointerStartY);
      if (deltaX < 10 && deltaY < 10) {
        this.setExpanded(true);
      }
    }

    this._releasePointerCaptureIfNeeded(event);
    this._isDragging = false;
  }

  private _releasePointerCaptureIfNeeded(event: PointerEvent): void {
    if (this._capturedPointerId !== event.pointerId) return;
    this._releasePointerCaptureIfAny();
  }

  private _releasePointerCaptureIfAny(): void {
    const panel = this._panelEl;
    const id = this._capturedPointerId;
    if (panel && id !== null && panel.hasPointerCapture(id)) {
      panel.releasePointerCapture(id);
    }
    this._capturedPointerId = null;
  }
}
